// Drift Team Website 2025
// src/contexts/SettingsContext.jsx
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 23.04.2025 to add fallback settings and improve error handling
// Updated again on 23.04.2025 to handle non-critical errors gracefully

import { createContext, useState, useEffect, useContext, useRef } from 'react';
import { get } from '../utils/api';
import { getUserLanguage } from '../utils/language';

export const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // Pouze pro kritické chyby
  const language = getUserLanguage();
  const isFetching = useRef(false);

  // Statický fallback pro nastavení
  const fallbackSettings = {
    hero_title: 'NO RISK NO DRIFT',
    hero_description: language === 'cs' ? 'Vítejte na webu našeho driftového týmu!' : 'Welcome to our drift team website!',
    hero_video_path: '/images/videos/hero-background.mp4',
    contact_email: 'info@norisknodrift.com',
    contact_phone: '+420 123 456 789',
    contact_address: 'Česká Lípa, Czech Republic',
    instagram_url: 'https://www.instagram.com/norisknodrift/',
    facebook_url: 'https://www.facebook.com/NoRiskNoDrift/',
    youtube_url: 'https://www.youtube.com/@norisknodrift',
    tiktok_url: 'https://www.tiktok.com/@norisknodrift',
  };

  const fetchWithRetry = async (url, headers, retries = 3, delay = 2000) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const data = await get(url, headers);
        return data;
      } catch (err) {
        if ((err.message.includes('429') || err.message.includes('Network error')) && attempt < retries) {
          console.warn(`[SettingsContext] Retry (${attempt}/${retries}) after ${delay}ms: ${err.message}`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        throw err;
      }
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      if (isFetching.current) {
        console.log('[SettingsContext] Fetch already in progress, skipping');
        return;
      }

      isFetching.current = true;
      const cacheKey = `settings_${language}`;
      const cacheExpiryKey = `${cacheKey}_expiry`;
      let cachedSettings = null;

      try {
        cachedSettings = localStorage.getItem(cacheKey);
        const cacheExpiry = localStorage.getItem(cacheExpiryKey);

        // Kontrola cache (expirace 30 minut)
        if (cachedSettings && cacheExpiry && Date.now() < parseInt(cacheExpiry)) {
          console.log('[SettingsContext] Using cached settings:', { cacheKey });
          setSettings(JSON.parse(cachedSettings));
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('[SettingsContext] Error reading cache:', err.message);
        // Pokračujeme bez chyby, použijeme API nebo fallback
      }

      try {
        console.log('[SettingsContext] Fetching settings from API:', { language });
        const headers = { 'accept-language': language };
        const data = await fetchWithRetry('/settings', headers);

        // Mapování nastavení do objektu
        const settingsMap = Array.isArray(data)
          ? data.reduce((acc, setting) => {
              const translation = setting.translations?.[language] || setting.translations?.['en'] || {};
              acc[setting.setting_key] = translation.setting_value || fallbackSettings[setting.setting_key] || '';
              return acc;
            }, {})
          : { ...fallbackSettings };

        // Uložení do cache
        try {
          localStorage.setItem(cacheKey, JSON.stringify(settingsMap));
          localStorage.setItem(cacheExpiryKey, Date.now() + 30 * 60 * 1000); // 30 minut expirace
          console.log('[SettingsContext] Cached settings:', { cacheKey });
        } catch (err) {
          console.error('[SettingsContext] Error saving to cache:', err.message);
        }

        setSettings(settingsMap);
        setError(null); // Žádná chyba, pokud máme data
      } catch (err) {
        console.error('[SettingsContext] Error fetching settings:', err.message);
        // Rozlišení kritických a nekritických chyb
        if (err.message.includes('Unauthorized') || err.message.includes('Failed to parse response')) {
          // Kritická chyba - nastavíme error
          setError(err.message || 'Failed to load settings');
        } else {
          // Nekritická chyba (např. síťový problém) - použijeme fallback bez chyby
          console.warn('[SettingsContext] Using fallback settings due to:', err.message);
          setError(null); // Nezobrazíme chybu uživateli
        }
        setSettings(fallbackSettings); // Vždy použijeme fallback
      } finally {
        setLoading(false);
        isFetching.current = false;
      }
    };

    loadSettings();
  }, [language]);

  return (
    <SettingsContext.Provider value={{ settings, loading, error }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};